/**
 * Created by staffanrantzow on 05/04/16.
 */

import axios from 'axios';
import UserEntity from './user-entity';
import AddressEntity from '../address/address-entity';
import store from 'store';
import { setUser } from 'redux/modules/user/index';
import { proxyCounterpart } from '../../scripts/proxyCounterpart';
import { LocalStorageStore } from
  'interactive-solutions/authentication/service/local-storage-store';
import CookieUtils from 'frilans-finans/utils/cookie-utils';
import PendingRequestEntity from '../entities/pending-request-entity';
import Raven from 'raven-js';
import { TaxkeyService } from 'frilans-finans';
import { setLoggedInInvoiceUserTaxKeyId } from 'redux/modules/invoice/index';

class UserService {

  constructor () {
    this.currentUser = null;
    this.store = new LocalStorageStore();
  }

  hydrate (user) {
    let attr = user.data.attributes;

    Raven.setUserContext({
      email: attr.email,
      id: user.data.id
    });

    return new UserEntity(
      user.data.id,
      attr.account_clearing_number,
      attr.account_number,
      attr.api_consumer_id,
      attr.autogen,
      attr.bankgiro,
      attr.cellphone,
      attr.co,
      attr.company_name,
      attr.created_at,
      attr.default_taxkey_id,
      attr.email,
      attr.email_confirmed,
      attr.first_name,
      attr.foreclosure,
      attr.last_login,
      attr.last_name,
      attr.loyalty_points,
      attr.postgiro,
      attr.profession_title,
      attr.profession_id,
      attr.role,
      attr.show_reg_invoice_page,
      attr.social_security_number,
      attr.telephone,
      attr.user_id_owner,
      attr.verify_password,
      attr.addedlatest,
      attr.bic,
      attr.receive_email_notifications,
      attr.iban,
      attr.image_src,
      attr.itp,
      attr.lang,
      attr.preferred_payment_type_id,
      attr.show_name_in_network,
      attr.display_rotrut,
      attr.receive_sms_notifications,
      attr.vacationpay,
      attr.vacationpay_gross,
      attr.international_account,
      attr.can_change_itp,
      attr.save_itp,
      attr.itp_rate,
      attr.save_vacation_pay,
      new AddressEntity(
        attr.city,
        attr.co,
        attr.country,
        attr.street,
        attr.zip
      ),
      attr.bank
    );
  }

  extractParams (params) {
    return {
      data: {
        attributes: {
          first_name: params.firstName,
          last_name: params.lastName,
          email: params.email,
          cellphone: params.cellphone,
          password: params.password,
          social_security_number: params.identityNumber
        }
      }
    };
  }

  post (params) {
    return axios.request({
      url: 'backend://users?url=' + location.origin + '/activate-account',
      method: 'POST',
      data: this.extractParams(params)
    });
  }

  getError (error) {
    let response = error.response;

    if (response && response.status === 422) {
      let errorMessages = [];
      for (let error of error.response.data.errors) {
        if (error.detail.hasOwnProperty('first_name') &&
          error.detail.first_name === 'iso20022text') {
          errorMessages.push(proxyCounterpart('FIRST_NAME_ERROR_INVALID_CHARACTERS'));
        }
        if (error.detail.hasOwnProperty('last_name') &&
          error.detail.last_name === 'iso20022text') {
          errorMessages.push(proxyCounterpart('LAST_NAME_ERROR_INVALID_CHARACTERS'));
        }
        if (error.detail.hasOwnProperty('errors')) {
          if (error.detail.errors.hasOwnProperty('account_number') &&
            error.detail.errors.account_number === 'accountnumber:account_clearing_number') {
            errorMessages.push(proxyCounterpart('ACCOUNT_CLEARING_NUMBER_ERROR'));
          }
          if (error.detail.errors.hasOwnProperty('iban')) {
            errorMessages.push(proxyCounterpart('IBAN_ERROR'));
          }
          if (error.detail.errors.hasOwnProperty('bic')) {
            errorMessages.push(proxyCounterpart('BIC_ERROR'));
          }
          if (error.detail.errors.hasOwnProperty('cellphone')) {
            errorMessages.push(proxyCounterpart('TELEPHONE_ERROR'));
          }
          if (error.detail.errors.hasOwnProperty('email') &&
            (error.detail.errors.email === 'uniq:user' ||
              error.detail.errors.email === 'uniqperclientorapiconsumer:user')
          ) {
            errorMessages.push(proxyCounterpart('EMAIL_ALREADY_REGISTERED'));
          }
          if (error.detail.errors.hasOwnProperty('social_security_number') &&
            error.detail.errors.social_security_number === 'uniqwith:user,primary_account,1') {
            errorMessages.push(proxyCounterpart('SSNR_ALREADY_REGISTERED'));
          }
        }
        if (error.detail.hasOwnProperty('current_password') &&
          error.detail.current_password[0] === 'Wrong password') {
          errorMessages.push(proxyCounterpart('CURRENT_PASSWORD_ERROR'));
        }
      }

      if (errorMessages.length > 0) {
        return errorMessages;
      }
    }

    return false;
  }

  hasErrorFor (attribute, error) {
    let response = error.response;

    if (response && response.status === 422) {
      for (let error of response.data.errors) {
        if (error.detail.hasOwnProperty('errors')) {
          if (error.detail.errors.hasOwnProperty(attribute)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  getParams (params) {
    let obj = {};

    if (params.firstName) {
      obj.first_name = params.firstName;
    }
    if (params.lastName) {
      obj.last_name = params.lastName;
    }
    if (params.cellphone) {
      obj.cellphone = params.cellphone;
    }
    if (params.email) {
      obj.email = params.email;
    }
    if (params.street) {
      obj.street = params.street;
    }
    if (params.hasOwnProperty('co')) {
      obj.co = params.co;
    }
    if (params.city) {
      obj.city = params.city;
    }
    if (params.zip) {
      obj.zip = params.zip;
    }
    if (params.country) {
      obj.country = params.country;
    }
    if (params.bank) {
      obj.bank = params.bank;
    }
    if (params.bic) {
      obj.bic = params.bic;
    }
    if (params.iban) {
      obj.iban = params.iban;
    }
    if (params.internationalAccount) {
      obj.international_account = params.internationalAccount;
    }
    if (params.accountClearingNr) {
      obj.account_clearing_number = params.accountClearingNr;
    }
    if (params.socialSecurityNr) {
      obj.social_security_number = params.socialSecurityNr;
    }
    if (params.accountNr) {
      obj.account_number = params.accountNr;
    }
    if (params.defaultTaxkeyId) {
      obj.default_taxkey_id = params.defaultTaxkeyId;
    }
    if (params.hasOwnProperty('displayHsd')) {
      obj.display_rotrut = params.displayHsd;
    }
    if (params.professionId) {
      obj.profession_id = params.professionId;
    }
    if (params.hasOwnProperty('saveITP')) {
      obj.save_itp = params.saveITP;
    }
    if (params.hasOwnProperty('itpRate')) {
      obj.itp_rate = parseFloat(params.itpRate);
    }
    if (params.hasOwnProperty('saveVacationPay')) {
      obj.save_vacation_pay = params.saveVacationPay;
    }
    if (params.hasOwnProperty('loyaltyPoints')) {
      obj.loyalty_points = params.loyaltyPoints;
    }
    if (params.password) {
      obj.password = params.password;
    }
    if (params.currentPassword) {
      obj.current_password = params.currentPassword;
    }
    if (params.hasOwnProperty('receiveSmsNotifications')) {
      obj.receive_sms_notifications = params.receiveSmsNotifications;
    }
    if (params.hasOwnProperty('receiveEmailNotifications')) {
      obj.receive_email_notifications = params.receiveEmailNotifications;
    }

    return {
      data: {
        attributes: obj
      }
    };
  }

  update (params) {
    return axios.patch('backend://me', this.getParams(params))
      .then((response) => {
        var user = this.hydrate(response.data);
        store.dispatch(setUser(user));
        CookieUtils.setNameCookie(user.fullName);
        return user;
      });
  }

  updateSsnr (params) {
    return axios.patch('backend://me', this.getParams(params))
      .then((response) => {
        var user = this.hydrate(response.data);
        CookieUtils.setNameCookie(user.fullName);
        return user;
      }).then((user) => {
        TaxkeyService.get(user.id);
        return user;
      }).then((user) => {
        store.dispatch(setLoggedInInvoiceUserTaxKeyId(user.defaultTaxkeyId));
        store.dispatch(setUser(user));
        return user;
      });
  }

  get () {
    if (this.currentUser) {
      let pendingRequest = new PendingRequestEntity();
      pendingRequest.promise = Promise.resolve(this.currentUser);
      return pendingRequest;
    } else {
      return this.fetchUser();
    }
  }

  fetchUser () {
    let oauth = this.store.getOauth();

    let pendingRequest = new PendingRequestEntity();

    if (oauth) {
      pendingRequest.promise = axios.get(
        'backend://me', {
          cancelToken: new axios.CancelToken(function executor (c) {
            pendingRequest.cancel = c;
          })
        }
      )
      .then(
        (response) => {
          this.currentUser = this.hydrate(response.data);
          store.dispatch(setUser(this.currentUser));
          CookieUtils.setNameCookie(this.currentUser.fullName);

          return this.currentUser;
        }
      );

      return pendingRequest;
    }
    pendingRequest.promise = Promise.reject();
    return pendingRequest;
  }

  clearUser () {
    this.currentUser = null;
    Raven.setUserContext();
  }

  resetPassword (email) {
    return axios.post('backend://reset-password', {
      data: {
        attributes: {
          email: email,
          url: location.origin + '/set-new-password'
        }
      }
    });
  }

  changePassword (id, token, password) {
    return axios.post(`backend://users/${id}/change-password`, {
      data: {
        attributes: {
          token: token,
          password: password
        }
      }
    });
  }

  activateAccount (id, token) {
    return axios.get(`backend://users/${id}/confirm-email/${token}`, {
      data: {
        attributes: {
          token: token
        }
      }
    });
  }

  getHoursWorked (userId, from, to) {
    return axios.get(`backend://users/${userId}/hours-worked-sum?from_date=${from}&to_date=${to}`);
  }

  saveUserRegistrationUtmTags (token, utmParams) {
    try {
      const source = utmParams.hasOwnProperty('utm_source') ? utmParams['utm_source'] : '';
      const medium = utmParams.hasOwnProperty('utm_medium') ? utmParams['utm_medium'] : '';
      const campaign = utmParams.hasOwnProperty('utm_campaign') ? utmParams['utm_campaign'] : '';
      const content = utmParams.hasOwnProperty('utm_content') ? utmParams['utm_content'] : '';
      const term = utmParams.hasOwnProperty('utm_term') ? utmParams['utm_term'] : '';

      if (source.length || medium.length || campaign.length || content.length || term.length) {
        return axios.post('backend://utm-tag', {
          data: {
            attributes: {
              source: source,
              medium: medium,
              campaign: campaign,
              content: content,
              term: term
            }
          }
        },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }
    } catch (_) {}
  }
}

export default new UserService();



/** WEBPACK FOOTER **
 ** ./src/frilans-finans/user/user-service.js
 **/